import OntosError from '../../model/OntosError'
import {Component, Vue} from "vue-facing-decorator";
import {CredentialError} from "@/errors/errors";
import {notApproved} from "@/types/Access";
import {t} from "@/i18n";

//Classe-base, e não componente: não leva toNative. Quem o aplica é a folha da cadeia,
//que é o que de fato vira componente registrável.
@Component({emits: ['loaded', 'showMessage', 'showErrorMessage']})
class RouteComponent extends Vue {

    loaded = false
    isSaving = false
    isLoading = true

    /**
     * Os parâmetros da rota, como texto.
     *
     * O vue-router 4+ tipa cada parâmetro como `string | string[]`, porque um parâmetro
     * repetível (`:id+`) vira lista. Nenhuma rota deste app tem parâmetro repetível, então
     * aqui é sempre texto — e o tipo largo só obrigaria cada tela a um cast.
     */
    get params(): Record<string, string> {
        return this.$route.params as Record<string, string>
    }

    get baseProps() {
        return {
            loading: this.isLoading,
            saving: this.isSaving
        }
    }
    private async privateLoad() {
        const self = this as any;
        if (self.load) await self.load()
    }

    private async privateAfterLoad() {
        const self = this as any;
        if (self.afterLoad) await self.afterLoad()
    }

    async mounted() {
        this.loaded = false
        const before = new Date()
        this.isLoading = true
        try {
            await this.privateLoad()
            this.loaded = true
            this.$emit('loaded')
            await this.privateAfterLoad()
        } catch (err) {
            if (this.leftOut(err)) await this.$router.push({name: 'login'})
            else {
                this.$emit('showErrorMessage', err.message)
            }
            console.error(err)
        } finally {
            console.info(`Load time for ${JSON.stringify(this.$route.name)} = ${new Date().getTime() - before.getTime()}`)
            this.isLoading = false
        }
    }

    errorCaptured(err: any) {
        this.handleError(err)
        return false
    }

    handleError(err: any) {
        //Solto, e não dentro de um objeto: `console.log({handleError: err})` deixava o
        //erro colapsado, e copiá-lo do console dava "{}" — um Error não tem propriedade
        //própria enumerável, então o JSON some com a mensagem e com a pilha.
        console.error('handleError:', err)
        if (this.leftOut(err)) this.$router.push({name: 'login'})

        else if (err instanceof OntosError || err?.name === 'OntosError') {
            this.$emit('showErrorMessage', err.message)
            console.error(err)
        } else {
            const wrapped = new OntosError(t('error.unexpected'), err)
            this.$emit('showErrorMessage', wrapped.message)
            console.error(wrapped)
        }
    }

    /**
     * A sessão deixou de valer, e a tela não tem o que fazer além de mandar ao login.
     *
     * São dois casos, e o segundo é novo. O token caiu — `CredentialError`. E **a conta deixou
     * de ser admitida**: alguém a devolveu a pendente enquanto ela estava aberta, e desde então
     * o servidor recusa tudo. Sem isto, essa pessoa veria "Erro inesperado" em cada tela que
     * abrisse, sem nada que explicasse o que mudou; no login, a frase que já existe explica.
     *
     * Nos dois lugares que perguntavam a mesma coisa — o `mounted` e o `handleError` —, porque
     * uma resposta só não pode divergir da outra.
     */
    private leftOut(err: any): boolean {
        return err instanceof CredentialError || notApproved(err)
    }

    info(message: any) {
        this.$emit('showMessage', message)
    }

    throwError(err: any, cause?: any) {
        const error = new OntosError(err, cause)
        this.handleError(error)
        throw error
    }

    async saving(fun: Function) {
        try {
            this.isSaving = true
            return await fun()
        } finally {
            this.isSaving = false
        }
    }

    /**
     * O campo está preenchido? Devolve a mensagem de erro, ou vazio — a forma que o
     * `:error-messages` do Vuetify espera.
     *
     * Aqui porque já estava escrita igual no login e no cadastro, e a tela de acesso seria a
     * terceira cópia. É a única regra de campo escrita no app, e de propósito: **formato não se
     * confere com expressão regular aqui**. Quem sabe dizer se algo é um e-mail é o próprio
     * campo `type="email"`, pelo `validity.typeMismatch` do navegador — e, em último caso, o
     * servidor, cuja recusa chega no campo.
     */
    required(text: string): string {
        return text ? '' : this.$tt('required-message') as string
    }

}

export default RouteComponent
